import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { CourseWithGrades } from '../../../types';

interface GradesTabProps {
  selectedCourse: CourseWithGrades;
}

export const GradesTab: React.FC<GradesTabProps> = ({ selectedCourse }) => {
  // Create combined grade components from assignments and exams
  const createCombinedGradeComponents = () => {
    const combinedComponents: any[] = [];

    // Add assignments
    if (selectedCourse.assignments) {
      selectedCourse.assignments.forEach((assignment) => {
        if (assignment.grade !== undefined && assignment.grade !== null) {
          combinedComponents.push({
            id: `assignment-${assignment.id}`,
            name: assignment.assignmentName,
            type: 'assignment',
            weight: assignment.assignmentWeight,
            score: assignment.grade,
            isActualGrade: true,
          });
        }
      });
    }

    // Calculate total weight of assignments and non-completion exams
    let totalFixedWeight = 0;
    const nonCompletionExams: any[] = [];
    const completionExams: any[] = [];

    // First pass: collect assignments weight
    if (selectedCourse.assignments) {
      selectedCourse.assignments.forEach((assignment) => {
        totalFixedWeight += assignment.assignmentWeight || 0;
      });
    }

    // Second pass: process exams and separate completion vs non-completion
    if (selectedCourse.exams) {
      selectedCourse.exams.forEach((exam) => {
        // Find highest scoring session
        let highestSession: any = null;
        let highestGrade = -1;

        exam.sessions.forEach((session) => {
          if (
            session.grade !== undefined &&
            session.grade !== null &&
            session.grade > highestGrade
          ) {
            highestGrade = session.grade;
            highestSession = session;
          }
        });

        if (highestSession && highestGrade >= 0) {
          const examComponent = {
            id: `exam-${exam.id}`,
            name: exam.examName,
            type: 'exam',
            weight: exam.points || 0,
            score: highestGrade,
            isActualGrade: true,
            completionGrade: exam.completionGrade,
            sessionId: highestSession.id,
          };

          if (exam.completionGrade) {
            completionExams.push(examComponent);
          } else {
            nonCompletionExams.push(examComponent);
            totalFixedWeight += exam.points || 0;
          }
        }
      });
    }

    // Add non-completion exams
    combinedComponents.push(...nonCompletionExams);

    // Calculate dynamic weight for completion exams
    const remainingWeight = Math.max(0, 100 - totalFixedWeight);
    const completionExamsCount = completionExams.length;

    if (completionExamsCount > 0) {
      const dynamicWeight = remainingWeight / completionExamsCount;
      completionExams.forEach((exam) => {
        exam.weight = Math.round(dynamicWeight * 100) / 100; // Round to 2 decimal places
        combinedComponents.push(exam);
      });
    }

    // Add original grade components (if any) that are not actual grades
    if (selectedCourse.gradeComponents) {
      selectedCourse.gradeComponents.forEach((component) => {
        combinedComponents.push({
          ...component,
          isActualGrade: false,
        });
      });
    }

    return combinedComponents;
  };

  const allGradeComponents = createCombinedGradeComponents();

  const componentTypeTemplate = (rowData: any) => {
    if (rowData.isActualGrade) {
      const typeLabel = rowData.type === 'assignment' ? 'מטלה' : 'מבחן';
      return (
        <Tag
          value={typeLabel}
          severity={rowData.type === 'assignment' ? 'info' : 'warning'}
        />
      );
    } else {
      // Original grade component
      const typeLabel = rowData.type === 'assignment' ? 'מטלה' : 'מבחן';
      return (
        <Tag
          value={typeLabel}
          severity={rowData.type === 'assignment' ? 'info' : 'warning'}
        />
      );
    }
  };

  const gradeTemplate = (value: number) => {
    return <span className="grade-value">{value}</span>;
  };

  const statusBodyTemplate = (status: string) => {
    const getStatusSeverity = (status: string) => {
      switch (status) {
        case 'לומד':
          return 'success';
        case 'נרשם':
          return 'info';
        case 'ביטל':
          return 'danger';
        case 'עזב':
          return 'warning';
        case 'סיים':
          return 'success';
        case 'דופלם':
          return 'info';
        default:
          return 'warning';
      }
    };

    const getStatusIcon = (status: string) => {
      switch (status) {
        case 'לומד':
          return 'pi pi-user';
        case 'נרשם':
          return 'pi pi-check-circle';
        case 'ביטל':
          return 'pi pi-times-circle';
        case 'עזב':
          return 'pi pi-sign-out';
        case 'סיים':
          return 'pi pi-check-square';
        case 'דופלם':
          return 'pi pi-star';
        default:
          return 'pi pi-question';
      }
    };

    return (
      <Tag
        value={status}
        severity={getStatusSeverity(status)}
        icon={getStatusIcon(status)}
        className="program-status-tag"
      />
    );
  };

  return (
    <div>
      <div className="course-summary">
        <div className="course-summary-item">
          <span className="summary-label">מצב קורס:</span>
          <span>{statusBodyTemplate(selectedCourse.status)}</span>
        </div>
        <div className="course-summary-item">
          <span className="summary-label">כמות שעות:</span>
          <span>{selectedCourse.courseHours}</span>
        </div>
        <div className="course-summary-item">
          <span className="summary-label">נוכחות מוחלטת:</span>
          <span>{gradeTemplate(selectedCourse.absoluteGrade)}</span>
        </div>
        <div className="course-summary-item">
          <span className="summary-label">נוכחות יחסית:</span>
          <span>{gradeTemplate(selectedCourse.relativeGrade)}</span>
        </div>
        <div className="course-summary-item">
          <span className="summary-label">ציון סופי:</span>
          <span className="grade-final">
            {gradeTemplate(selectedCourse.finalScore)}
          </span>
        </div>
      </div>

      <Divider />

      <div className="dialog-grade-components">
        <h3>מרכיבי הציון</h3>
        <DataTable
          value={allGradeComponents}
          className="grade-components-table"
          responsiveLayout="stack"
          breakpoint="768px"
          showGridlines
          dir="rtl"
        >
          <Column field="name" header="שם המרכיב" sortable />
          <Column
            field="type"
            header="סוג"
            body={(rowData) => componentTypeTemplate(rowData)}
            sortable
          />
          <Column
            field="weight"
            header="משקל"
            body={(rowData) => `${rowData.weight}%`}
            sortable
            dataType="numeric"
          />
          <Column
            field="score"
            header="ציון"
            body={(rowData) =>
              rowData.score !== undefined && rowData.score !== null
                ? gradeTemplate(rowData.score)
                : '-'
            }
            sortable
            dataType="numeric"
          />
        </DataTable>

        <div className="pie-chart-placeholder">
          <div className="pie-chart-title">התפלגות ציון לפי מרכיבים</div>
          <div className="pie-chart">
            {allGradeComponents.map((component) => (
              <div
                key={component.id}
                className={`chart-segment ${component.type}`}
                style={{ width: `${component.weight}%` }}
                title={`${component.name}: ${component.weight}%`}
              >
                {component.weight >= 10 && `${component.weight}%`}
              </div>
            ))}
          </div>
          <div className="chart-legend">
            <div className="legend-item">
              <div className="legend-color assignment"></div>
              <div className="legend-text">מטלות</div>
            </div>
            <div className="legend-item">
              <div className="legend-color exam"></div>
              <div className="legend-text">מבחנים</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
